import {
  normalizeTrackerType,
  TRACKER_TYPES,
} from './trackerTypes';

export const TRACKERS_COLLECTION = 'trackers';
export const TRACKER_EVIDENCE_COLLECTION = 'trackerEvidence';

export const TRACKER_SOURCE_TYPES = Object.freeze({
  CARE_SOURCE: 'careSource',
  TIMELINE_ENTRY: 'timelineEntry',
  PROFILE_FIELD: 'profileField',
  PROFILE_GOAL: 'profileGoal',
  KNOWLEDGE_ITEM: 'knowledgeItem',
  MANUAL: 'manual',
});

export const TRACKER_STATUSES = Object.freeze({
  ACTIVE: 'active',
  PAUSED: 'paused',
  ARCHIVED: 'archived',
});

const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const VALID_TRACKER_STATUSES = new Set(Object.values(TRACKER_STATUSES));
const VALID_TRACKER_SOURCE_TYPES = new Set(Object.values(TRACKER_SOURCE_TYPES));

export const buildTrackerSourceRef = ({
  type = '',
  id = '',
  fieldPath = '',
  goalId = '',
  relationshipActive,
  path = '',
  text = '',
} = {}) => {
  const normalizedType = normalizeText(type);
  const normalizedId = normalizeText(id);

  if (!VALID_TRACKER_SOURCE_TYPES.has(normalizedType) || !normalizedId) {
    return null;
  }

  return {
    type: normalizedType,
    id: normalizedId,
    ...(goalId ? { goalId: normalizeText(goalId) } : {}),
    ...(typeof relationshipActive === 'boolean' ? { relationshipActive } : {}),
    ...(fieldPath ? { fieldPath: normalizeText(fieldPath) } : {}),
    ...(path ? { path: normalizeText(path) } : {}),
    ...(text ? { text: normalizeText(text) } : {}),
  };
};

const normalizeTrackerSource = (source = null, fallbackTimelineEntryId = '') => {
  if (source && typeof source === 'object' && !Array.isArray(source)) {
    return buildTrackerSourceRef(source);
  }

  const timelineEntryId = normalizeText(fallbackTimelineEntryId);
  return timelineEntryId
    ? buildTrackerSourceRef({
      type: TRACKER_SOURCE_TYPES.TIMELINE_ENTRY,
      id: timelineEntryId,
      path: `dailyLogs/${timelineEntryId}`,
    })
    : null;
};

export const normalizeTrackerStatus = (value = '') => {
  const normalized = normalizeText(value).toLowerCase();
  return VALID_TRACKER_STATUSES.has(normalized)
    ? normalized
    : TRACKER_STATUSES.ACTIVE;
};

export const buildTrackerPayload = ({
  childId = '',
  type = TRACKER_TYPES.ROUTINE_HABIT,
  title = '',
  description = '',
  status = TRACKER_STATUSES.ACTIVE,
  source = null,
  createdBy = '',
  createdAt,
  updatedAt,
} = {}) => ({
  schemaVersion: 1,
  childId: normalizeText(childId),
  type: normalizeTrackerType(type) || TRACKER_TYPES.ROUTINE_HABIT,
  title: normalizeText(title) || 'Tracker',
  description: normalizeText(description),
  status: normalizeTrackerStatus(status),
  source: normalizeTrackerSource(source),
  createdBy: normalizeText(createdBy),
  createdAt,
  updatedAt,
});

export const buildTrackerEvidencePayload = ({
  childId = '',
  trackerId = '',
  goalId = '',
  goalRelationshipActive,
  timelineEntryId = '',
  summary = '',
  value = null,
  unit = '',
  occurredAt = null,
  entryDayKey = '',
  source = null,
  createdBy = '',
  createdAt,
  updatedAt,
} = {}) => ({
  schemaVersion: 1,
  childId: normalizeText(childId),
  trackerId: normalizeText(trackerId),
  ...(normalizeText(goalId) ? { goalId: normalizeText(goalId) } : {}),
  ...(typeof goalRelationshipActive === 'boolean' ? { goalRelationshipActive } : {}),
  timelineEntryId: normalizeText(timelineEntryId),
  summary: normalizeText(summary),
  value,
  unit: normalizeText(unit),
  occurredAt,
  entryDayKey: normalizeText(entryDayKey),
  source: normalizeTrackerSource(source, timelineEntryId),
  createdBy: normalizeText(createdBy),
  createdAt,
  updatedAt,
});
